import update from 'immutability-helper';
import _ from 'lodash';

import { FILES_ACTIONS } from '../../../redux/files/files.actions';
import {
  JobEventPayload,
  JobProcessingFailedPayload,
  JOBS_ACTION_NAMES,
  JobStartFailedPayload,
} from '../../../redux/jobs/jobs.actions';
import { Action } from '../../../redux/redux.types';
import { GroupFilesUploadingPayload } from '../../../types/media-assets';
import { MaybeUndef, StringMap } from '../../../types/system';
import {
  COMPRESS_FLOW_ACTION_NAMES,
  CompressFilesPayload,
} from '../../file-manager/compress-flow/compress-flow.actions';
import {
  FILE_MANAGER_ACTIONS,
  MultipleDownloadPayload,
} from '../../file-manager/redux/file-manager.actions';

import {
  JobOperationStatus,
  JobStartRequestStatus,
} from './common/compress/job-based-group.types';
import { CompressOperationGroup } from './compress-group/download-group.types';
import { MultipleDownloadOperationGroup } from './download-group/download-group.types';
import { OPERATION_GROUPS_ACTION_NAMES } from './operation-groups.actions';
import {
  JobOperationContext,
  OperationGroup,
  OperationGroupType,
  UploadOperationGroup,
} from './operation-groups.types';

export interface OperationGroupsState {
  groupsMap: StringMap<OperationGroup>;
  groupsOrderList: string[];
}

function isCompressOperationGroup(
  operationGroup: OperationGroup,
): operationGroup is CompressOperationGroup {
  return operationGroup.type === OperationGroupType.Compress;
}

function isUploadOperationGroup(
  operationGroup: OperationGroup,
): operationGroup is UploadOperationGroup {
  return operationGroup.type === OperationGroupType.Upload;
}

const defaultState: OperationGroupsState = {
  groupsMap: {},
  groupsOrderList: [],
};

function addGroupToState(
  state: OperationGroupsState,
  group: OperationGroup,
): OperationGroupsState {
  return {
    groupsMap: {
      ...state.groupsMap,
      [group.id]: group,
    },
    groupsOrderList: [...state.groupsOrderList, group.id],
  };
}

function updateJobStartRequestStatus(
  state: OperationGroupsState,
  context: MaybeUndef<JobOperationContext>,
  newStatus: JobOperationStatus,
): OperationGroupsState {
  // Job is not under Operation Group context so just skip this action
  if (!context) {
    return state;
  }

  const { groupId }: JobOperationContext = context;
  const group = state.groupsMap[groupId];

  if (!group) {
    return state;
  }

  switch (group.type) {
    case OperationGroupType.MultipleDownload:
    case OperationGroupType.Compress: {
      const updatedGroup:
        | MultipleDownloadOperationGroup
        | CompressOperationGroup = {
        ...group,
        jobStartRequestStatus: newStatus,
      };

      return {
        ...state,
        groupsMap: {
          ...state.groupsMap,
          [groupId]: updatedGroup,
        },
      };
    }

    default:
      return state;
  }
}

export const operationGroups = (
  state: OperationGroupsState = defaultState,
  { type, payload }: Action,
): OperationGroupsState => {
  switch (type) {
    case FILES_ACTIONS.FILE_GROUP_UPLOAD_START: {
      const { groupId, clientFileIds }: GroupFilesUploadingPayload = payload;

      return addGroupToState(state, {
        type: OperationGroupType.Upload,
        id: groupId,
        fileClientIdsList: clientFileIds,
      });
    }

    case FILE_MANAGER_ACTIONS.DOWNLOAD_MULTIPLE_START: {
      // Create operation group though job create request is not yet sent.
      // But we still want to show operation group to the user (with pending status)

      const { batchClientId, sourceFileIds }: MultipleDownloadPayload = payload;

      return addGroupToState(state, {
        type: OperationGroupType.MultipleDownload,
        id: batchClientId,
        fileIdsList: sourceFileIds,
        jobStartRequestStatus: {
          status: JobStartRequestStatus.Starting,
        },
      });
    }

    case COMPRESS_FLOW_ACTION_NAMES.COMPRESS_FILES_START: {
      // Create operation group though job create request is not yet sent.
      // But we still want to show operation group to the user (with pending status)

      const { batchClientId, sourceFileIds }: CompressFilesPayload = payload;

      return addGroupToState(state, {
        type: OperationGroupType.Compress,
        id: batchClientId,
        fileIdsList: sourceFileIds,
        jobStartRequestStatus: {
          status: JobStartRequestStatus.Starting,
        },
      });
    }

    case JOBS_ACTION_NAMES.JOB_STARTED: {
      // Job request has completed so we can set jobId for the operation group
      const typedPayload: JobEventPayload<any> = payload;
      return updateJobStartRequestStatus(state, typedPayload.context, {
        status: JobStartRequestStatus.Started,
        jobId: typedPayload.job.id as string,
      });
    }

    case JOBS_ACTION_NAMES.JOB_START_FAILED: {
      // Job request has failed so set error status
      const { context, error }: JobStartFailedPayload = payload;
      return updateJobStartRequestStatus(state, context, {
        status: JobStartRequestStatus.StartFailed,
        error,
      });
    }

    case JOBS_ACTION_NAMES.JOB_PROCESSING_FAILED: {
      // Job request has failed so set error status
      const { context, job, error }: JobProcessingFailedPayload<any> = payload;
      return updateJobStartRequestStatus(state, context, {
        status: JobStartRequestStatus.ProcessingFailed,
        error,
        jobId: job.id as string,
      });
    }

    case OPERATION_GROUPS_ACTION_NAMES.CLOSE_GROUP: {
      const { groupId } = payload;

      return {
        groupsMap: _.omit(state.groupsMap, [groupId]),
        groupsOrderList: _.without(state.groupsOrderList, groupId),
      };
    }

    case FILES_ACTIONS.FILE_DELETE_SUCCESS: {
      const groupsMap = _(state.groupsMap)
        .values()
        .map((operationGroup) => {
          if (isCompressOperationGroup(operationGroup)) {
            const fileIdsList = _.filter(
              operationGroup.fileIdsList,
              (fileId: string) => {
                return fileId !== payload.id;
              },
            );

            return {
              ...operationGroup,
              fileIdsList,
            };
          }

          if (isUploadOperationGroup(operationGroup)) {
            const fileClientIdsList = _.filter(
              operationGroup.fileClientIdsList,
              (clientId: string) => {
                return clientId !== payload.clientId;
              },
            );

            return {
              ...operationGroup,
              fileClientIdsList,
            };
          }

          return operationGroup;
        })
        .filter((operationGroup) => {
          if (isCompressOperationGroup(operationGroup)) {
            return _.size(operationGroup.fileIdsList) > 0;
          }

          if (isUploadOperationGroup(operationGroup)) {
            return _.size(operationGroup.fileClientIdsList) > 0;
          }

          return true;
        })
        .keyBy('id')
        .value() as StringMap<OperationGroup>;

      return {
        groupsMap,
        groupsOrderList: _.keys(groupsMap),
      };
    }

    case JOBS_ACTION_NAMES.JOB_STATUS_CHANGED:
      if (!payload.fileId) {
        return state;
      }

      const groupsMap = update(state.groupsMap, {
        [payload.context.groupId]: {
          fileIdsList: { $set: [payload.fileId] },
        },
      }) as StringMap<OperationGroup>;

      return {
        ...state,
        groupsMap,
      };

    default:
      return state;
  }
};
